// RightSidebar.js
import React, { useEffect, useState } from "react";
import { createPanelParticipant, getPanelParticipantsByPanelId, removePanelParticipant } from "app/apis/panels";
import { FaTimes } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { getProsecutorListByAdminUnitAndOrgUnit } from "app/apis/users";
import { CIVIL_CASE_DIRECTOR_ROLE, ROLES_FILTERING_FOR_ADDING_PROSECUTOR_TO_PANEL, WOREDA_ATTORNEY_OFFICE_HEAD, ZONAL_ATTORNEY_OFFICE_HEAD } from "app/utils/const";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "app/contexts/AppContext";
import EmployeeProfile from "app/views/common_component/EmployeeProfile";
import { useTranslation } from "react-i18next";
import { getEthiopicDateTime } from "app/utils/custom_functions";

const RightSidebar = () => {
    const { t } = useTranslation();
    const { selectedPanel, setSelectedPanel } = useAppContext();
    const id = selectedPanel?.uu_id;
    const [panelParticipants, setPanelParticipants] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [availableMembers, setAvailableMembers] = useState([]);
    const [popupSearchTerm, setPopupSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [additionalNotes, setAdditionalNotes] = useState(t("additional note"));
    const [removalReason, setRemovalReason] = useState(t("The participant is removed."));
    const ORG_UNIT = sessionStorage.getItem('org_unit');
    const ADMIN_UNIT = sessionStorage.getItem('admin_unit');
    const employee_id = sessionStorage.getItem('employee_id');
    const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
    const current_user_role = sessionStorage.getItem('roles') || "";
    const isDirectorOrHead = current_user_role.includes(CIVIL_CASE_DIRECTOR_ROLE) || current_user_role.includes(ZONAL_ATTORNEY_OFFICE_HEAD) || current_user_role.includes(WOREDA_ATTORNEY_OFFICE_HEAD);

    useEffect(() => {
        if (!id) return;
        const fetchPanelParticipants = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await getPanelParticipantsByPanelId(id);
                setPanelParticipants(response.data);
            } catch (error) {
                setError(t("Failed to fetch panel participants."));
            } finally {
                setIsLoading(false);
            }
        };
        fetchPanelParticipants();
    }, [id, t]);

    function filterByRoles(data, roles) {
        return data.filter(item => item.roles.some(role => roles.includes(role)));
    }

    useEffect(() => {
        const fetchAvailableMembers = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await getProsecutorListByAdminUnitAndOrgUnit(ADMIN_UNIT, ORG_UNIT);
                const filteredProsecutor = filterByRoles(response?.data, ROLES_FILTERING_FOR_ADDING_PROSECUTOR_TO_PANEL);
                setAvailableMembers(filteredProsecutor || []);
            } catch (error) {
                setError(t("Failed to fetch available members."));
            } finally {
                setIsLoading(false);
            }
        };
        fetchAvailableMembers();
    }, [isPopupOpen, id, ADMIN_UNIT, ORG_UNIT, t]);

    const filteredParticipants = searchTerm
        ? panelParticipants.filter((user) =>
            `${user.participant?.first_name} ${user.participant?.last_name}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        : panelParticipants;

    const filteredAvailableMembers = availableMembers.filter((member) =>
        `${member?.user?.first_name} ${member?.user?.last_name}`
            .toLowerCase()
            .includes(popupSearchTerm.toLowerCase())
    );

    const handleMemberSelection = (member) => {
        setSelectedMember({
            ...member,
            participant: member
        });
    };

    const handleAddMember = async () => {
        if (selectedMember) {
            const isParticipantAlreadyAdded = panelParticipants.some(
                (participant) => participant?.participant === selectedMember?.employee_id
            );

            if (isParticipantAlreadyAdded) {
                setError(t("Participant is already added to the panel."));
                return;
            }
            setIsLoading(true);
            setError(null);
            try {
                const formData = new FormData();
                formData.append("panel", id);
                formData.append("participant", selectedMember?.employee_id);
                formData.append("additional_notes", additionalNotes);

                const response = await createPanelParticipant(formData);
                const newParticipant = response?.data;
                setSelectedPanel(selectedPanel);
                setPanelParticipants([...panelParticipants, newParticipant]);

                setSelectedMember(null);
                setAdditionalNotes(t("additional note"));
                setIsPopupOpen(false);
            } catch (error) {
                console.error("Error adding member:", error);
                setError(t("Failed to add member."));
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleRemoveMember = async (participantId) => {
        setIsLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append("removed", true);
            formData.append("removal_reason", removalReason);
            formData.append("removed_by", employee_id);

            await removePanelParticipant(participantId, formData);

            setPanelParticipants(panelParticipants.map(participant =>
                participant.uu_id === participantId
                    ? { ...participant, removed: true, removal_reason: removalReason, removed_by: employee_id }
                    : participant
            ));
            setSelectedPanel(selectedPanel);
            setRemovalReason(t("The participant is removed."));
            setIsConfirmPopupOpen(false);
        } catch (error) {
            console.error("Error removing member:", error);
            setError(t("Failed to remove member."));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {selectedPanel?.is_approved && (
                <div className="w-[25vw] bg-white p-4 border-l">
                    <h2 className="text-[22] font-semibold">{t("Panel Settings List")}</h2>
                    <div className="bg-black text-white p-3 rounded mt-2">
                        {t("Case Reg. No")}: #{selectedPanel?.case || t("N/A")}
                    </div>
                    <div className="bg-gray-300 text-black p-3 rounded mt-2">
                        {t("Priority")}: {selectedPanel?.priority_level || t("Not Set")}
                    </div>
                    <div className="bg-gray-300 text-black p-3 rounded mt-2">
                        {t("Starts")}: {selectedPanel?.starts_at ? getEthiopicDateTime({ datetime: selectedPanel.starts_at }) : t("N/A")}
                    </div>
                    <div
                        className={`p-3 rounded mt-2 text-black ${selectedPanel
                            ? selectedPanel.closed
                                ? "bg-red-500"
                                : "bg-green-500"
                            : "bg-gray-300"
                            }`}
                    >
                        {t("Status")}: {selectedPanel ? (selectedPanel.closed ? t("Closed") : t("Open")) : t("Unknown")}
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                        <h3 className="font-semibold">{t("Panel Members")} ({panelParticipants?.length})</h3>
                        {isDirectorOrHead && !selectedPanel?.closed && (
                            <button
                                className="px-3 py-1 text-[18] bg-gray-200 border rounded hover:bg-gray-300"
                                onClick={() => setIsPopupOpen(true)}
                            >
                                + {t("Add Member")}
                            </button>
                        )}
                    </div>

                    {isLoading ? (
                        <p className="text-gray-500 text-[18] mt-2">{t("Loading...")}</p>
                    ) : error ? (
                        <p className="text-red-500 text-[18] mt-2">{error}</p>
                    ) : (
                        <div className="mt-2 max-h-60 overflow-y-auto">
                            {filteredParticipants?.length > 0 ? (
                                filteredParticipants?.map((user, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between mt-2 p-2 border rounded"
                                    >
                                        <div className="flex items-center gap-2">
                                            <EmployeeProfile request={user?.participant} />
                                        </div>

                                        {isDirectorOrHead && !selectedPanel?.closed && user?.participant != selectedPanel?.created_by && employee_id != user?.participant && (
                                            <div className="relative group">
                                                <button
                                                    onClick={() => {
                                                        setSelectedMember(user?.uu_id);
                                                        setIsConfirmPopupOpen(true);
                                                    }}
                                                    disabled={user?.removed}
                                                    className={`ml-2 px-2 py-1 text-white rounded-full text-[18] font-bold 
                                                        ${user?.removed ? "bg-gray-400 cursor-not-allowed" : "bg-red-500 hover:bg-red-600"}`}
                                                >
                                                    X
                                                </button>

                                                {user?.removed && (
                                                    <span className="absolute bottom-full left-1 transform -translate-x-1/2 mb-2 hidden group-hover:block 
                                                        bg-gray-700 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                                        {t("Already removed")}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-[18] mt-2">{t("No matching members found.")}</p>
                            )}
                        </div>
                    )}

                    {isConfirmPopupOpen && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 400 }}>
                            <div className="bg-white p-6 rounded-lg w-96 shadow-lg">
                                <h2 className="text-[22] font-semibold">{t("Confirm Removal")}</h2>
                                <p className="text-[18] text-gray-500 mt-2">{t("Are you sure you want to remove this member?")}</p>
                                <textarea
                                    placeholder={t("Enter reason (optional)")}
                                    className="w-full p-2 mt-3 border rounded"
                                    value={removalReason}
                                    onChange={(e) => setRemovalReason(e.target.value)}
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        className="px-4 py-2 mr-2 bg-gray-300 rounded"
                                        onClick={() => setIsConfirmPopupOpen(false)}
                                    >
                                        {t("Cancel")}
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-red-500 text-white rounded"
                                        onClick={() => handleRemoveMember(selectedMember)}
                                    >
                                        {t("Remove")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {isPopupOpen && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 400 }}>
                            <div className="bg-white p-6 rounded-lg w-96 shadow-lg">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-[22] font-semibold">{t("Adding Panel Members")}</h2>
                                    <button onClick={() => setIsPopupOpen(false)}>
                                        <FaTimes className="text-gray-500" />
                                    </button>
                                </div>
                                <p className="text-[18] text-gray-500 mt-1">
                                    {t("Add panel members to the panel. You can search and select members from the list below.")}
                                </p>

                                <h3 className="mt-4 font-medium">{t("Select Panel Member")}</h3>
                                <div className="mt-2 z-[10]">
                                    <FiSearch className="absolute left-3 top-3 text-gray-400" style={{ zIndex: -1 }} />
                                    <input
                                        type="text"
                                        placeholder={t("Search Name")}
                                        className="w-full p-2 pl-10 border rounded"
                                        value={popupSearchTerm}
                                        onChange={(e) => setPopupSearchTerm(e.target.value)}
                                        style={{ zIndex: -1 }}
                                    />
                                </div>

                                <div className="mt-4 max-h-[40vh] overflow-y-auto">
                                    {filteredAvailableMembers.map((member, index) => (
                                        member?.user?.employee_id != selectedPanel?.created_by && (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between mt-2 p-2 border rounded hover:bg-gray-100 cursor-pointer"
                                                onClick={() => handleMemberSelection(member?.user)}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <EmployeeProfile request={member?.user?.employee_id} />
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>

                                {selectedMember && (
                                    <div className="mt-4">
                                        <div className="flex items-center bg-gray-100 p-3 rounded">
                                            <div className="flex items-center gap-2">
                                                <EmployeeProfile request={selectedMember?.employee_id} />
                                            </div>
                                            <button className="ml-auto text-red-500" onClick={() => setSelectedMember(null)}>
                                                <FaTimes />
                                            </button>
                                        </div>

                                        <div className="mt-4">
                                            <label className="block text-[18] font-medium text-gray-700">{t("Additional Notes")}</label>
                                            <textarea
                                                placeholder={t("Enter additional notes")}
                                                className="w-full p-2 mt-1 border rounded"
                                                value={additionalNotes}
                                                onChange={(e) => setAdditionalNotes(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                <button
                                    className="w-full mt-6 p-2 bg-blue-900 text-white rounded-lg"
                                    onClick={handleAddMember}
                                >
                                    {t("Add Member")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default RightSidebar;